import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import List from "./List";
import SettingsCard from "./SettingsCard";
import { fetchGisTrials, fetchGisTrialStats } from "../../../redux/slice/gisTrialSlice";

const StatTile = ({ label, value, hint, tone = "" }) => (
    <div className="bg-white rounded-2 common-shadow p-3 h-100">
        <div className="text-muted text-uppercase small fw-medium mb-1">{label}</div>
        <div className={`fs-3 fw-semibold lh-1 ${tone}`}>{value}</div>
        {hint && <div className="text-muted small mt-1">{hint}</div>}
    </div>
);

const GisTrial = () => {
    const dispatch = useDispatch();

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Subscription / </span>GIS Trial
            </h4>
        </div>
    );

    const { trials = [], total, stats, isLoading } = useSelector(
        (state) => state.gisTrial || {}
    );

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState({ value: 10, label: 10 });

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchData = useCallback(() => {
        dispatch(
            fetchGisTrials({
                search: debouncedSearchTerm,
                status: statusFilter,
                page: currentPage,
                limit: limit?.value,
            })
        );
        dispatch(fetchGisTrialStats());
    }, [dispatch, debouncedSearchTerm, statusFilter, currentPage, limit?.value]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleStatusChange = (value) => {
        setStatusFilter(value);
        setCurrentPage(1);
    };

    const paginationConfig = useMemo(() => {
        const pageSize = limit?.value || 10;
        return {
            total: total || 0,
            totalPages: Math.ceil((total || 0) / pageSize),
            currentPage,
            setCurrentPage,
            pageSize,
            setPageSize: (size) => {
                setLimit({ value: size, label: size });
                setCurrentPage(1);
            },
            onPageChange: (page) => setCurrentPage(page),
        };
    }, [total, currentPage, limit]);

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <Container fluid className="px-0">
                <Row className="g-3 mt-1">
                    <Col md={3} sm={6}>
                        <StatTile label="Trials started" value={stats?.total ?? "-"} />
                    </Col>
                    <Col md={3} sm={6}>
                        <StatTile
                            label="Active now"
                            value={stats?.active ?? "-"}
                            tone="text-success"
                        />
                    </Col>
                    <Col md={3} sm={6}>
                        <StatTile label="Expired" value={stats?.expired ?? "-"} />
                    </Col>
                    <Col md={3} sm={6}>
                        <StatTile
                            label="Converted"
                            value={stats?.converted ?? "-"}
                            hint={
                                stats
                                    ? `${stats.conversion_rate}% of trials led to a paid plan`
                                    : null
                            }
                            tone="text-primary"
                        />
                    </Col>
                </Row>

                <div className="mt-4">
                    <SettingsCard />
                </div>

                <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
                    <List
                        data={trials}
                        isLoading={isLoading}
                        searchTerm={searchTerm}
                        setSearchTerm={handleSearchChange}
                        statusFilter={statusFilter}
                        setStatusFilter={handleStatusChange}
                        currentPage={currentPage}
                        limit={limit?.value}
                        pagination={paginationConfig}
                    />
                </div>
            </Container>
        </>
    );
};

export default GisTrial;
